import { __decorate } from "tslib";
import { ArrayDecoder, field, StringDecoder } from '@common/encoding';
import { Formatter } from '@stamhoofd/utility';
import { Sorter } from '@stamhoofd/utility';
import { BalanceItemRelationType } from '../BalanceItem.js';
import { BalanceItemPaymentDetailed } from '../BalanceItemDetailed.js';
import { BaseOrganization } from '../Organization.js';
import { PrivatePayment } from './Payment.js';
export class PaymentGeneral extends PrivatePayment {
    balanceItemPayments = [];
    /**
     * Only set for administrators with the correct permissions
     */
    stripeAccountId = null;
    /**
     * Only set for administrators with the correct permissions
     */
    payingOrganization = null;
    get calculatedPrice() {
        return this.balanceItemPayments.reduce((total, item) => total + item.price, 0);
    }
    get groupIds() {
        const ids = this.balanceItemPayments.flatMap((p) => {
            const id = p.balanceItem.relations.get(BalanceItemRelationType.Group)?.id;
            return id ? [id] : [];
        });
        return Formatter.uniqueArray(ids);
    }
    get webshopIds() {
        const ids = this.balanceItemPayments.flatMap((p) => {
            const id = p.balanceItem.relations.get(BalanceItemRelationType.Webshop)?.id;
            return id ? [id] : [];
        });
        return Formatter.uniqueArray(ids);
    }
    get memberNames() {
        const ids = this.balanceItemPayments.flatMap((p) => {
            const id = p.balanceItem.relations.get(BalanceItemRelationType.Member)?.name.toString();
            return id ? [id] : [];
        });
        return Formatter.joinLast(Formatter.uniqueArray(ids), ', ', ' ' + $t(`%M1`) + ' ');
    }
    getBalanceItemPaymentsHtmlTable() {
        return getBalanceItemPaymentsHtmlTable(this.balanceItemPayments);
    }
    /**
     * Customer name, phone, payment method and total price
     */
    getPaymentDataHTMLTable() {
        let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><tbody>`;
        const customer = this.customer;
        const replacements = [];
        if (customer) {
            if (customer.company) {
                replacements.push({
                    title: customer.company.VATNumber || customer.company.companyNumber ? $t(`%1JI`) : $t('%1PW'),
                    value: customer.company.name,
                });
                if (customer.company.address) {
                    replacements.push({
                        title: customer.company.VATNumber || customer.company.companyNumber ? $t(`%MW`) : $t('%1Sh'),
                        value: customer.company.address.toString(),
                    });
                }
                if (customer.company.VATNumber) {
                    replacements.push({
                        title: $t(`%1CK`),
                        value: customer.company.VATNumber,
                    });
                }
                else if (customer.company.companyNumber) {
                    replacements.push({
                        title: $t(`%wa`),
                        value: customer.company.companyNumber,
                    });
                }
                else {
                    replacements.push({
                        title: $t(`%wa`),
                        value: $t('%1FW') + ' (' + $t('%1QY') + ')',
                    });
                }
            }
            else {
                replacements.push({
                    title: $t(`%1Os`),
                    value: customer.dynamicName,
                });
            }
            if (customer.phone) {
                replacements.push({
                    title: $t(`%18Z`),
                    value: customer.phone,
                });
            }
            if (customer.email) {
                replacements.push({
                    title: $t(`%1FK`),
                    value: customer.email,
                });
            }
        }
        else {
            // Information missing - default to email recipient data
            replacements.push({
                title: $t(`%1Os`),
                value: '{{firstName}} {{lastName}}', // will get replaced automatically by the email template system
            });
        }
        for (const replacement of replacements) {
            if (replacement.value.length === 0) {
                continue;
            }
            str += `<tr><td><h4>${Formatter.escapeHtml(replacement.title)}</h4></td><td>${Formatter.escapeHtml(replacement.value)}</td></tr>`;
        }
        return str + '</tbody></table>';
    }
    /** Groups equal payment descriptions and prefixes repeated descriptions with their count. */
    getShortDescription() {
        let shortDescriptions = this.balanceItemPayments.filter(p => p.price !== 0).map(p => p.balanceItem.paymentShortDescription).filter(p => p !== null);
        if (shortDescriptions.length === 0) {
            // Include free items
            shortDescriptions = this.balanceItemPayments.map(p => p.balanceItem.paymentShortDescription).filter(p => p !== null);
        }
        // Count the number of times each description occurs and add prefix if more than 1
        const counts = new Map();
        for (const shortDescription of shortDescriptions) {
            counts.set(shortDescription, (counts.get(shortDescription) ?? 0) + 1);
        }
        // Add prefix if more than 1
        const arr = [];
        // Keep the former object's ordering for descriptions that are array-index strings.
        const index = (key) => String(Number(key) >>> 0) === key && Number(key) < 2 ** 32 - 1 ? Number(key) : Infinity;
        const sortedCounts = [...counts].sort(([a], [b]) => index(a) - index(b));
        for (const [shortDescription, count] of sortedCounts) {
            if (count > 1) {
                arr.push(count + ' x ' + shortDescription);
            }
            else {
                arr.push(shortDescription);
            }
        }
        return Formatter.capitalizeFirstLetter(Formatter.joinLast(arr, ', ', ' ' + $t(`%M1`) + ' '));
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(BalanceItemPaymentDetailed) })
], PaymentGeneral.prototype, "balanceItemPayments", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 198 })
], PaymentGeneral.prototype, "stripeAccountId", void 0);
__decorate([
    field({ decoder: BaseOrganization, nullable: true, version: 344 })
], PaymentGeneral.prototype, "payingOrganization", void 0);
export function getBalanceItemPaymentsHtmlTable(balanceItemPayments) {
    const payments = balanceItemPayments.slice().sort((a, b) => {
        return Sorter.stack(Sorter.byNumberValue(a.price, b.price), Sorter.byStringValue(a.itemDescription ?? a.balanceItem.name, b.itemDescription ?? b.balanceItem.name));
    });
    let str = '';
    str += `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><tbody>`;
    for (const payment of payments) {
        const title = payment.itemTitle;
        let description = Formatter.escapeHtml(payment.itemDescription ?? '');
        if (payment.quantity !== 1) {
            if (description) {
                description += `\n`;
            }
            description += `${Formatter.escapeHtml(Formatter.float(payment.quantity))} x ${Formatter.escapeHtml(Formatter.price(payment.unitPrice))}`;
        }
        const descriptionText = description ? `<p class="email-style-description-small pre-wrap">${description}</p>` : '';
        const titleColumn = `<td><h4 class="email-style-title-list">${Formatter.escapeHtml(title)}</h4>${descriptionText}</td>`;
        const price = payment.price === 0 ? $t('%1Mn') : Formatter.price(payment.price);
        const priceColumn = `<td>${Formatter.escapeHtml(price)}</td>`;
        str += `<tr>${titleColumn}${priceColumn}</tr>`;
    }
    return str + '</tbody></table>';
}
//# sourceMappingURL=PaymentGeneral.js.map